/**
 * Data layer for `composite-fraud-score-combine-multiple-vendor-scor`. Thin typed fetch wrappers over /api/v1/composite-fraud-score-combine-multiple-vendor-scor.
 * Raw `fetch()` is confined to this file per the dash contract.
 */

export interface CompositeFraudScoreCombineMultipleVendorScorRow {
	id: string;
	tenant_id: string;
	created_at: number;
	updated_at: number;
	[key: string]: unknown;
}

export interface ListCompositeFraudScoreCombineMultipleVendorScorResponse {
	count: number;
	items: CompositeFraudScoreCombineMultipleVendorScorRow[];
}

export interface CreateCompositeFraudScoreCombineMultipleVendorScorBody {
	[key: string]: unknown;
}

const BASE = "/api/v1/composite-fraud-score-combine-multiple-vendor-scor";

async function parseOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			data = text;
		}
	}
	if (!res.ok) {
		const msg =
			data &&
			typeof data === "object" &&
			data !== null &&
			"error" in data &&
			typeof (data as { error: unknown }).error === "string"
				? (data as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return data as T;
}

export async function list(): Promise<ListCompositeFraudScoreCombineMultipleVendorScorResponse> {
	const res = await fetch(BASE, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<ListCompositeFraudScoreCombineMultipleVendorScorResponse>(res);
}

export async function create(body: CreateCompositeFraudScoreCombineMultipleVendorScorBody): Promise<CompositeFraudScoreCombineMultipleVendorScorRow> {
	const res = await fetch(BASE, {
		method: "POST",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify(body ?? {}),
	});
	return parseOrThrow<CompositeFraudScoreCombineMultipleVendorScorRow>(res);
}

export async function remove(id: string): Promise<{ removed: boolean }> {
	const res = await fetch(`${BASE}/${encodeURIComponent(id)}`, {
		method: "DELETE",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<{ removed: boolean }>(res);
}

export const compositeFraudScoreCombineMultipleVendorScorQueryKeys = {
	all: ["composite-fraud-score-combine-multiple-vendor-scor"] as const,
	list: () => [...compositeFraudScoreCombineMultipleVendorScorQueryKeys.all] as const,
	detail: (id: string) => [...compositeFraudScoreCombineMultipleVendorScorQueryKeys.all, id] as const,
};
